/**
 * The opening-hours editor's state as the help assistant's `opening_hours` fill value, and back (owner request
 * 2026-10-18; design §31.9.3 amended; `packages/domain/src/help/opening-hours-fill.ts` holds the value's rules).
 *
 * The wizard's week editor and the agent page's "When it answers" card both bind their opening hours with these, so a
 * proposal reads and writes the same thing on either screen:
 *
 * - {@link openingHoursFillOf} reports the editor's whole state — every day's shifts (a blank or unfinished shift left
 *   out, so the value always reads), the zone, every special date — for the page context and for an undo.
 * - {@link weekOfFill} and {@link specialDateOfFill} turn a whole state (the registry merged the change set into it)
 *   back into the editor's own shapes, which the screen hands to its own setters.
 * - {@link openingHoursFillProblemOf} is the screen's `accepts`: what the editor itself would refuse — more shifts than
 *   it draws, a zone its select does not offer, a NEW special date already past, more dates than it keeps.
 */
import { ISO_DAY_CODE, OPENING_HOURS_LABEL_CHAR_LIMIT, OPENING_HOURS_WHOLE_DAY_SHIFT, type OpeningHoursFill, type OpeningHoursSpecialDate } from "@wetalk/domain";

import { ALL_DAY_PERIOD, SPECIAL_DATE_LIMIT, isAllDay, timeZoneOptionOf, type OpeningPeriod, type OpeningWeek, type SpecialDateEntry } from "./opening-hours.js";

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/u;

/** One editor period as a fill shift, or `null` for one the editor would not accept yet (blank, unfinished, empty). */
function shiftOf(period: OpeningPeriod): string | null {
  if (isAllDay(period)) return OPENING_HOURS_WHOLE_DAY_SHIFT;
  if (!TIME.test(period.opensAt) || !TIME.test(period.closesAt) || period.opensAt === period.closesAt) return null;
  return `${period.opensAt}-${period.closesAt}`;
}

function periodOf(shift: string): OpeningPeriod {
  if (shift === OPENING_HOURS_WHOLE_DAY_SHIFT) return ALL_DAY_PERIOD;
  const [opensAt = "", closesAt = ""] = shift.split("-");
  return { opensAt, closesAt };
}

function shiftsOf(period: readonly OpeningPeriod[]): readonly string[] {
  return [...new Set(period.map(shiftOf).filter((one): one is string => one !== null))];
}

/** The editor's whole state as a fill value. `timeZone` `null`: not known yet, left out. */
export function openingHoursFillOf(week: OpeningWeek, timeZone: string | null, specialDate: readonly SpecialDateEntry[]): OpeningHoursFill {
  const day: { [K in (typeof ISO_DAY_CODE)[number]]?: readonly string[] } = {};
  for (const [index, code] of ISO_DAY_CODE.entries()) day[code] = shiftsOf(week[index] ?? []);
  const special: OpeningHoursSpecialDate[] = specialDate.map((one) => ({
    date: one.date,
    label: Array.from(one.label).slice(0, OPENING_HOURS_LABEL_CHAR_LIMIT).join(""),
    interval: shiftsOf(one.interval),
  }));
  return { ...(timeZone === null ? {} : { time_zone: timeZone }), day, special_date: special };
}

/** The week a whole state names; a day it does not name keeps its periods. */
export function weekOfFill(week: OpeningWeek, value: OpeningHoursFill): OpeningWeek {
  return ISO_DAY_CODE.map((code, index) => {
    const shift = value.day?.[code];
    return shift === undefined ? (week[index] ?? []) : shift.map(periodOf);
  });
}

/** The special dates a whole state names, in the editor's shape. */
export function specialDateOfFill(value: OpeningHoursFill): readonly SpecialDateEntry[] | null {
  if (value.special_date === undefined) return null;
  return value.special_date.map((one) => ({ date: one.date, label: one.label, interval: one.interval.map(periodOf) }));
}

/**
 * Why the editor would refuse this whole state, or `null`: more than `maxShift` shifts in a day, a zone the select does
 * not offer, a special date not already listed that is before `today`, more than the editor keeps.
 */
export function openingHoursFillProblemOf(
  value: OpeningHoursFill,
  held: { readonly timeZone: string | null; readonly specialDate: readonly SpecialDateEntry[]; readonly today: string; readonly maxShift: number },
): "too_many_shifts" | "time_zone_not_offered" | "date_passed" | "too_many_dates" | null {
  for (const code of ISO_DAY_CODE) if ((value.day?.[code]?.length ?? 0) > held.maxShift) return "too_many_shifts";
  for (const one of value.special_date ?? []) if (one.interval.length > held.maxShift) return "too_many_shifts";
  if (value.time_zone !== undefined && value.time_zone !== held.timeZone && !timeZoneOptionOf(held.timeZone ?? "UTC").includes(value.time_zone)) {
    return "time_zone_not_offered";
  }
  const known = new Set(held.specialDate.map((one) => one.date));
  if ((value.special_date ?? []).some((one) => !known.has(one.date) && one.date < held.today)) return "date_passed";
  if ((value.special_date?.length ?? 0) > SPECIAL_DATE_LIMIT) return "too_many_dates";
  return null;
}
